import { useCallback, useEffect, useRef, useState } from 'react';

import { analytics, type QuickSearchOpenSource } from '@/analytics';

export type { QuickSearchOpenSource };

export interface UseQuickSearchShortcutResult {
    enabled: boolean;
    isOpen: boolean;
    open: (source: QuickSearchOpenSource) => void;
    close: () => void;
    toggle: () => void;
}

export function useQuickSearchShortcut(isAuthenticated: boolean): UseQuickSearchShortcutResult {
    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    const enabled = isAuthenticated;

    const setOpen = useCallback((next: boolean) => {
        isOpenRef.current = next;
        setIsOpen(next);
    }, []);

    const open = useCallback(
        (source: QuickSearchOpenSource) => {
            if (!enabled || isOpenRef.current) {
                return;
            }
            analytics.quickSearchOpened({ source });
            setOpen(true);
        },
        [enabled, setOpen]
    );
    const close = useCallback(() => setOpen(false), [setOpen]);
    const toggle = useCallback(() => {
        if (!enabled) {
            return;
        }
        if (isOpenRef.current) {
            setOpen(false);
            return;
        }
        analytics.quickSearchOpened({ source: 'shortcut' });
        setOpen(true);
    }, [enabled, setOpen]);

    useEffect(() => {
        if (!isAuthenticated) {
            setOpen(false);
        }
    }, [isAuthenticated, setOpen]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            const isK = event.key?.toLowerCase() === 'k';
            const isCmdOrCtrl = event.metaKey || event.ctrlKey;

            if (isCmdOrCtrl && isK) {
                event.preventDefault();
                toggle();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [toggle, enabled]);

    return { enabled, isOpen, open, close, toggle };
}
